import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Bell, Mail, Smartphone, Save } from 'lucide-react';
import { requestNotificationPermission } from '../utils/messaging';

interface NotificationPreferences {
  userId: string;
  email: {
    timelineUpdates: boolean;
    meetingAnnouncements: boolean;
    marketplaceMatches: boolean;
    inboxMessages: boolean;
  };
  push: {
    timelineUpdates: boolean;
    meetingAnnouncements: boolean;
    marketplaceMatches: boolean;
    inboxMessages: boolean;
  };
  fcmToken?: string;
}

const NotificationSettings: React.FC = () => {

  
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState<NotificationPermission>('default');

  const functions = getFunctions();

  useEffect(() => {
    if (user) {
      loadPreferences();
      checkPushSupport();
    }
  }, [user]);

  const checkPushSupport = () => {
    if ('Notification' in window) {
      setPushSupported(true);
      setPushPermission(Notification.permission);
    }
  };

  const loadPreferences = async () => {
    if (!user) return;

    try {
      const getNotificationPreferences = httpsCallable(functions, 'getNotificationPreferences');
      const result = await getNotificationPreferences({ userId: user.id });
      setPreferences(result.data as NotificationPreferences);
    } catch (error) {
      console.error('Error loading notification preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const requestPushPermission = async () => {
    console.log('Requesting push permission...');
    
    if (!('Notification' in window)) {
      console.log('Push notifications not supported');
              // Push notifications are not supported in this browser
      return;
    }

    try {
      console.log('Current notification permission:', Notification.permission);
      const permission = await Notification.requestPermission();
      console.log('Notification permission result:', permission);
      setPushPermission(permission);
      
      if (permission === 'granted' && user) {
        console.log('Permission granted, requesting FCM token...');
        // Request FCM token and register it
        await requestNotificationPermission(user);
        console.log('FCM token registration completed');
        // Reload preferences to get the updated FCM token
        await loadPreferences();
        console.log('Preferences reloaded');
        // Push notifications enabled successfully
      } else {
        console.log('Permission denied or no user');
      }
    } catch (error) {
      console.error('Error requesting push permission:', error);
              // Error enabling push notifications
    }
  };

  const updatePreference = (type: 'email' | 'push', key: string, value: boolean) => {
    if (!preferences) return;

    setPreferences({
      ...preferences,
      [type]: {
        ...preferences[type],
        [key]: value
      }
    });
  };

  const savePreferences = async () => {
    if (!preferences || !user) return;

    setSaving(true);
    try {
      const updateNotificationPreferences = httpsCallable(functions, 'updateNotificationPreferences');
      await updateNotificationPreferences(preferences);
              // Notification preferences saved successfully
    } catch (error) {
      console.error('Error saving notification preferences:', error);
              // Error saving notification preferences
    } finally {
      setSaving(false);
    }
  };



  if (loading) {
    return <div className="flex justify-center p-8">Loading notification settings...</div>;
  }

  if (!preferences) {
    return <div className="flex justify-center p-8">Error loading notification settings</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Notification Settings</h2>
        <Button onClick={savePreferences} disabled={saving} className="flex items-center space-x-2">
          <Save className="h-4 w-4" />
          <span>{saving ? 'Saving...' : 'Save Changes'}</span>
        </Button>
      </div>

      {/* Push Notification Setup */}
      {pushSupported && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Smartphone className="h-5 w-5" />
              <span>Push Notifications</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pushPermission === 'default' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Enable push notifications to receive instant updates on your device.
                </p>
                <Button onClick={requestPushPermission} className="w-full">
                  Enable Push Notifications
                </Button>
              </div>
            )}
            {pushPermission === 'granted' && (
              <div className="space-y-4">
                <div className="text-sm text-green-600">
                  ✓ Push notifications are enabled
                </div>
                {preferences.fcmToken && (
                  <div className="text-xs text-gray-500">
                    FCM Token: {preferences.fcmToken.substring(0, 20)}...
                  </div>
                )}
                
              </div>
            )}
            {pushPermission === 'denied' && (
              <div className="text-sm text-red-600">
                Push notifications are blocked. Please enable them in your browser settings.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Email Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Mail className="h-5 w-5" />
            <span>Email Notifications</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="email-timeline">Timeline Updates</Label>
                <p className="text-sm text-gray-600">New posts and updates on the timeline</p>
              </div>
              <Switch
                id="email-timeline"
                checked={preferences.email.timelineUpdates}
                onCheckedChange={(checked: boolean) => updatePreference('email', 'timelineUpdates', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="email-meetings">Meeting Announcements</Label>
                <p className="text-sm text-gray-600">New meetings and meeting updates</p>
              </div>
              <Switch
                id="email-meetings"
                checked={preferences.email.meetingAnnouncements}
                onCheckedChange={(checked: boolean) => updatePreference('email', 'meetingAnnouncements', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="email-marketplace">Marketplace Matches</Label>
                <p className="text-sm text-gray-600">New marketplace matches and opportunities</p>
              </div>
              <Switch
                id="email-marketplace"
                checked={preferences.email.marketplaceMatches}
                onCheckedChange={(checked: boolean) => updatePreference('email', 'marketplaceMatches', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="email-inbox">Inbox Messages</Label>
                <p className="text-sm text-gray-600">New messages in your inbox</p>
              </div>
              <Switch
                id="email-inbox"
                checked={preferences.email.inboxMessages}
                onCheckedChange={(checked: boolean) => updatePreference('email', 'inboxMessages', checked)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Push Notifications */}
      {pushSupported && pushPermission === 'granted' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Smartphone className="h-5 w-5" />
              <span>Push Notifications</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="push-timeline">Timeline Updates</Label>
                  <p className="text-sm text-gray-600">New posts and updates on the timeline</p>
                </div>
                <Switch
                  id="push-timeline"
                  checked={preferences.push.timelineUpdates}
                  onCheckedChange={(checked: boolean) => updatePreference('push', 'timelineUpdates', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="push-meetings">Meeting Announcements</Label>
                  <p className="text-sm text-gray-600">New meetings and meeting updates</p>
                </div>
                <Switch
                  id="push-meetings"
                  checked={preferences.push.meetingAnnouncements}
                  onCheckedChange={(checked: boolean) => updatePreference('push', 'meetingAnnouncements', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="push-marketplace">Marketplace Matches</Label>
                  <p className="text-sm text-gray-600">New marketplace matches and opportunities</p>
                </div>
                <Switch
                  id="push-marketplace"
                  checked={preferences.push.marketplaceMatches}
                  onCheckedChange={(checked: boolean) => updatePreference('push', 'marketplaceMatches', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="push-inbox">Inbox Messages</Label>
                  <p className="text-sm text-gray-600">New messages in your inbox</p>
                </div>
                <Switch
                  id="push-inbox"
                  checked={preferences.push.inboxMessages}
                  onCheckedChange={(checked: boolean) => updatePreference('push', 'inboxMessages', checked)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default NotificationSettings; 